import React, {useState} from "react";
import TextInput from "../form/TextInput";
import Icon from "../../ui/Icon";
import SwitchInput from "../form/SwitchInput";
import SelectOption from "../form/SelectOption";
import {GSTType} from "../../../enums/enums";

const getColumnValue = (row, column) => {
    const fieldParts = column.field.split(".");
    let value = row;
    for (const part of fieldParts) {
        if (value && value.hasOwnProperty(part)) {
            value = value[part];
        } else {
            value = null;
            break;
        }
    }
    return value;
};

const TableEdit = ({
                       className,
                       columns,
                       data,
                       idField,
                       searchFields = [],
                       searchQuery = "",
                       onSubmitChanges
                   }) => {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [editedData, setEditedData] = useState([]);

    const fs = className && className.includes("fs") ? "" : " fs-md";
    const tableSize = className && className.includes("table") ? "" : " table-sm";
    const classes = `table${tableSize}${fs}${className ? " " + className : ""}`;

    let filteredData = [...data];

    // search filter
    if (searchQuery.trim() && searchFields.length > 0) {
        filteredData = filteredData.filter((row) =>
            searchFields.some((field) => {
                const value = getColumnValue(row, {field});
                return (
                    value &&
                    value.toString().toLowerCase().includes(searchQuery.toLowerCase())
                );
            })
        );
    }

    // sort handler
    const handleSort = (column) => {
        if (column.disableSorting) return;
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    // sorted data
    const sortedData = [...filteredData];
    if (sortColumn) {
        sortedData.sort((a, b) => {
            if (sortColumn.field === "leaveDuration") {
                const dateA = new Date(a.leaveFrom);
                const dateB = new Date(b.leaveFrom);
                return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
            } else {
                const aValue = getColumnValue(a, sortColumn);
                const bValue = getColumnValue(b, sortColumn);
                if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
                if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
                return 0;
            }
        });
    }

// change handler
    const handleEditChange = (rowId, field, value) => {
        setEditedData(prev => {
            const processedValue =
                typeof value === "boolean"
                    ? value
                    : field === "gst" // keep GST as string
                        ? value
                        : isNaN(value) || value === ""
                            ? value
                            : Number(value);

            const existing = prev.find(item => item[idField] === rowId);
            let updatedItem;

            if (existing) {
                updatedItem = {...existing, [field]: processedValue};
            } else {
                updatedItem = {[idField]: rowId, [field]: processedValue};
            }

            // Auto-set availability when stock > 0
            if (field === "stock") {
                updatedItem["availability"] = Number(processedValue) > 0;
            }

            return existing
                ? prev.map(item => (item[idField] === rowId ? updatedItem : item))
                : [...prev, updatedItem];
        });
    };


    // submit handler
    const handleSubmit = () => {
        if (editedData.length === 0) return;
        onSubmitChanges(editedData);

        // Don’t clear immediately -> wait until parent confirms success
        // setEditedData([]);
    };

    return (
        <>
            <div className="table-responsive table-edit">
                <table className={classes}>
                    <thead>
                    <tr>
                        {columns.map((column, index) => (
                            <th
                                key={index}
                                onClick={() => handleSort(column)}
                                className={
                                    sortColumn === column
                                        ? `sort-arrow ${sortDirection}`
                                        : ""
                                }
                                style={{textAlign: column.align}}
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {sortedData.map((row, rowIndex) => {
                        const rowId = row[idField] ?? row.id ?? rowIndex;// fallback if no id
                        return (
                            <tr key={rowId}>
                                {columns.map((column, columnIndex) => {
                                    const originalValue = getColumnValue(row, column);
                                    const isEditable = column.editable;

                                    // Define editedValue for the current row & column
                                    const editedValue = editedData.find(item => item[idField] === rowId)?.[column.field];

                                    return (
                                        <td
                                            key={columnIndex}
                                            data-label={column.header + ": "}
                                        >
                                            {isEditable ? (
                                                column.type === "boolean" ? (
                                                    <SwitchInput
                                                        value={editedValue !== undefined ? editedValue : Boolean(originalValue)}
                                                        onChange={(e) => handleEditChange(rowId, column.field, e.target.checked)}
                                                    />
                                                ) : column.type === "select" ? (
                                                    <SelectOption
                                                        className="form-select form-select-sm"
                                                        value={editedValue ?? originalValue ?? ""}
                                                        enums={true}
                                                        options={GSTType}
                                                        onChange={(e) => handleEditChange(rowId, column.field, e.target.value)}
                                                    >
                                                    </SelectOption>
                                                ) : (
                                                    <TextInput
                                                        type={column.type === "number" ? "number" : "text"}
                                                        value={editedValue ?? originalValue ?? ""}
                                                        onChange={(e) =>
                                                            handleEditChange(rowId, column.field, e.target.value)
                                                        }
                                                        disabled={
                                                            column.field === "lowStockThreshold" &&
                                                            (editedData.find(item => item[idField] === rowId)?.stock ?? row.stock) === 0 &&
                                                            (editedData.find(item => item[idField] === rowId)?.lowStockThreshold ?? row.lowStockThreshold) == null
                                                        }
                                                    />
                                                )
                                            ) : column.render ? (
                                                column.render(row)
                                            ) : (
                                                originalValue
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="tableEditAction">
                <button className="btn bg-primary bg-opacity-75 rounded-pill py-3" onClick={handleSubmit}>
                    <Icon name="save" height="24" width="24"/>
                </button>
            </div>
        </>
    );
};

export default TableEdit;
